import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
  TextField,
  Box,
  Button,
  Typography,
} from "@mui/material";

/** List of delay reasons for Revised ECOE when date is future from today */
export const DELAY_REASON_OPTIONS = [
  "Appraisal Delay",
  "Construction Delay",
  "OSL Loan Delay",
  "Lennar Mortgage Loan Delay",
  "Home to Sell/Close Delay",
  "Homeowners Insurance Issues",
  "Other",
] as const;

export type DelayReasonOption = (typeof DELAY_REASON_OPTIONS)[number];

export const OTHER_REASON_MAX_LENGTH = 200;

const stopPropagation = (e: React.KeyboardEvent<HTMLDivElement>) => e.stopPropagation();
export interface DelayedReasonContentProps {
  onClose: () => void;
  onSave: (delayedReason: string) => void;
  initialDelayedReason?: string;
  /** When true, only Save is shown; panel stays open until user selects a reason and saves (no Cancel) */
  requireSelectionToClose?: boolean;
}

/** Inner content for Delayed Reason (used in both Dialog and Popover) */
export function DelayedReasonContent({
  onClose,
  onSave,
  initialDelayedReason = "",
  requireSelectionToClose = false,
}: DelayedReasonContentProps) {
  const [selectedReason, setSelectedReason] = useState<DelayReasonOption | null>(() => {
    const match = DELAY_REASON_OPTIONS.find((opt) => initialDelayedReason === opt || initialDelayedReason.startsWith(opt + " - "));
    return match ?? null;
  });
  const [otherText, setOtherText] = useState(() => {
    // Extract text from any reason with " - " suffix
    const match = DELAY_REASON_OPTIONS.find((opt) => initialDelayedReason.startsWith(opt + " - "));
    if (match) return initialDelayedReason.slice(match.length + 3);
    return "";
  });

  const handleSave = () => {
    if (!selectedReason) return;
    // For "Other", text is mandatory (will save only if text exists)
    // For others, text is optional
    if (selectedReason === "Other") {
      if (!otherText.trim()) return;
      const valueToSave = `Other - ${otherText.trim()}`;
      onSave(valueToSave);
      onClose();
    } else {
      // For other reasons, text is optional
      const valueToSave = otherText.trim()
        ? `${selectedReason} - ${otherText.trim()}`
        : selectedReason;
      onSave(valueToSave);
      onClose();
    }
  };

  const isOtherSelected = selectedReason === "Other";
  const otherError = otherText.length > OTHER_REASON_MAX_LENGTH;
  const otherCharactersLeft = OTHER_REASON_MAX_LENGTH - otherText.length;
  // For "Other", text is required; for others, it's optional
  const isSaveDisabled = !selectedReason || !!otherError || (isOtherSelected && !otherText.trim());

  return (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Delayed Reason
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 280 }}>
        <Autocomplete
          size="small"
          options={[...DELAY_REASON_OPTIONS]}
          value={selectedReason}
          onChange={(_, value) => {
            setSelectedReason((prev) => {
              if (prev !== value) {
                setOtherText("");
              }
              return value;
            });
          }}
          openOnFocus
          onKeyDown={stopPropagation}
          slotProps={{
            popper: { style: { zIndex: 10000 } },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Delay reason"
              placeholder="Select..."
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option} sx={{ py: 0.5 }}>
              <Chip label={option} size="small" variant="outlined" />
            </Box>
          )}
        />
        {selectedReason && (
          <TextField
            autoFocus
            size="small"
            placeholder={isOtherSelected ? "Add delay reason..." : "Add additional details (optional)..."}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            multiline
            maxRows={3}
            error={!!otherError}
            helperText={
              otherError
                ? `Max ${OTHER_REASON_MAX_LENGTH} characters`
                : `${otherCharactersLeft} characters left`
            }
            inputProps={{ maxLength: OTHER_REASON_MAX_LENGTH }}
            onKeyDown={stopPropagation}
            FormHelperTextProps={{ sx: { textAlign: "right" } }}
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
          {!requireSelectionToClose && (
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} variant="contained" size="small" disabled={isSaveDisabled}>
            Save
          </Button>
        </Box>
        {requireSelectionToClose && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Please select a delayed reason to continue.
          </Typography>
        )}
      </Box>
    </>
  );
}

interface RevisedEcoeDateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (delayedReason: string) => void;
  initialDelayedReason?: string;
  disableEnforceFocus?: boolean;
  disableAutoFocus?: boolean;
}

/**
 * Modal shown when Revised ECOE has a value that is future from today.
 * Dropdown (chips) to select Delayed Reason; "Other" includes an optional text field.
 */
export function RevisedEcoeDateModal({
  open,
  onClose,
  onSave,
  initialDelayedReason = "",
  disableEnforceFocus = false,
  disableAutoFocus = false,
}: RevisedEcoeDateModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      disableEnforceFocus={disableEnforceFocus}
      disableAutoFocus={disableAutoFocus}
      PaperProps={{
        sx: { borderRadius: 1, minWidth: 320 },
      }}
    >
      <DialogContent sx={{ pt: 2 }}>
        <DelayedReasonContent
          onClose={onClose}
          onSave={onSave}
          initialDelayedReason={initialDelayedReason}
        />
      </DialogContent>
    </Dialog>
  );
}
